import React from "react";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { MdSwapHoriz, MdSwapVert, MdAutorenew, MdDragIndicator } from "react-icons/md";
import { ENCLOSURE_ID, sectionSelectionId, useLayout } from "../../../Layouts/LayoutContext";
import { FiTrash2, FiChevronDown, FiChevronRight, FiExternalLink } from "react-icons/fi";
import { sectionMatchNames } from "../../../../builders/sectionMatch";
import { RAIL, RAIL_LABEL, normalizeRail, nextRail } from "../../../../builders/layoutMode";
import "./DeviceSectionTree.css";

// Resolves a SubGroupPlacement (sectionPlacements[i].subGroupPlacements[j])
// back to the real, editable device record. Per the backend model
// (SubGroupPlacement : Placement), the placement carries no name/article
// number of its own — its ReferenceId IS the id of the SubGroup document,
// the same id your devices array exposes as device.subGroup.id. That's the
// primary match; a couple of fallbacks are kept in case this is ever fed a
// differently-shaped entry.
function resolveDevice(entry, devices) {
    if (!entry || !devices?.length) return null;

    const subGroupId = entry.referenceId ?? entry.ReferenceId;

    if (subGroupId) {
        const bySubGroup = devices.find(d => d.subGroup?.id === subGroupId);
        if (bySubGroup) return bySubGroup;
    }

    if (entry.deviceId) {
        const byId = devices.find(d => d.id === entry.deviceId);
        if (byId) return byId;
    }

    const articleNumber = entry.articleNumber ?? entry.subGroup?.articleNumber;
    if (articleNumber) {
        const byArticle = devices.find(d => d.subGroup?.articleNumber === articleNumber);
        if (byArticle) return byArticle;
    }

    return null;
}

// SectionPlacement also has no name of its own - its ReferenceId points at
// the Section definition in the scenario (selectedScenario.sections).
function findSection(sectionPlacement, sections) {
    const refId = sectionPlacement.referenceId ?? sectionPlacement.ReferenceId;
    return sections?.find(s => (s.id ?? s.Id) === refId) ?? null;
}

function resolveSectionName(sectionPlacement, sections) {
    const match = findSection(sectionPlacement, sections);
    return match?.name ?? match?.Name ?? sectionPlacement.name ?? "Unnamed section";
}

const lc = (v) => (v ?? "").toString().trim().toLowerCase();

// Same order the layout engines use: SubGroupPlacement.Order, ascending.
const placementOrder = (entry) => {
    const o = entry?.order ?? entry?.Order;
    return typeof o === "number" ? o : Number.MAX_SAFE_INTEGER;
};

// Turns sectionPlacements + sections + devices into
// { name, rows: [{ device }] } per section.
//
// A device belongs to a section when
//   1) the layout already placed it there (SubGroupPlacement), OR
//   2) its SubGroup name is one of the section's match names (catalog mapping,
//      custom selection or own name - same rule as the layout builder, see
//      builders/sectionMatch.js). This is what makes devices that were just
//      imported / added show up immediately, before "Recalculate layout" has
//      created their placements.
//
// Rows are shown in the order the engine places them, so the user can see
// which consecutive devices form one rail block:
//   - by placement order (scenario SubGroup order); name-matched devices
//     without a placement yet are appended;
//   - with manualOrder (Flexible Oriented): device.sortOrder first, unsorted
//     devices after, in placement order - exactly the engine's rule.
function buildSections(sectionPlacements, sections, devices, manualOrder = false) {
    const placements = sectionPlacements ?? [];
    const defs = sections ?? [];
    const usedPlacements = new Set();
    const assigned = new Set(); // devices already shown via name matching

    const rowsFor = (placement, def) => {
        const names = new Set(sectionMatchNames(def).map(lc));
        const rows = new Map();

        const entries = [...(placement?.subGroupPlacements ?? placement?.SubGroupPlacements ?? [])]
            .sort((a, b) => placementOrder(a) - placementOrder(b));

        for (const entry of entries) {
            const device = resolveDevice(entry, devices);
            if (device && !rows.has(device.id)) rows.set(device.id, { device, order: placementOrder(entry) });
        }
        for (const device of devices ?? []) {
            if (rows.has(device.id) || assigned.has(device.id)) continue;
            if (names.has(lc(device.subGroup?.name))) {
                rows.set(device.id, { device, order: Number.MAX_SAFE_INTEGER });
                assigned.add(device.id);
            }
        }

        const list = Array.from(rows.values());
        if (manualOrder) {
            const manual = (r) => (typeof r.device.sortOrder === "number" ? r.device.sortOrder : Number.MAX_SAFE_INTEGER);
            list.sort((a, b) => manual(a) - manual(b) || a.order - b.order);
        }
        return list;
    };

    const result = [];

    // Driven by the scenario's sections, so a section shows up even if the
    // layout has not created a placement for it yet (e.g. right after import).
    for (const def of defs) {
        const defId = def.id ?? def.Id;
        const placement = placements.find(p => (p.referenceId ?? p.ReferenceId) === defId) ?? null;
        if (placement) usedPlacements.add(placement);
        result.push({
            key: placement ? (placement.id ?? placement.Id) : null,
            name: def.name ?? def.Name ?? "Unnamed section",
            rows: rowsFor(placement, def)
        });
    }

    // Placements whose section definition is gone: keep showing them.
    for (const placement of placements) {
        if (usedPlacements.has(placement)) continue;
        result.push({
            key: placement.id ?? placement.Id,
            name: resolveSectionName(placement, sections),
            rows: rowsFor(placement, null)
        });
    }

    return result.filter(section => section.rows.length > 0);
}

// The id every view (2D canvas, 3D canvas, tree) agrees on for a device.
function rowId(device) {
    return device?.subGroup?.id ?? device?.id;
}

// Only absolute http(s) links are opened (same rule as the backend's
// ConfiguratorUrlValidator), so a bad value can never run "javascript:" etc.
function safeConfiguratorUrl(url) {
    if (typeof url !== "string" || !url.trim()) return null;
    try {
        const parsed = new URL(url.trim());
        return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.href : null;
    } catch {
        return null;
    }
}

// Default lookup: the article's configuratorUrl, wherever the backend puts it
// on the device. Replace through the getConfiguratorUrl prop if your data
// links devices to articles differently.
const defaultGetConfiguratorUrl = (device) =>
    device?.configuratorUrl ??
    device?.article?.configuratorUrl ??
    device?.subGroup?.configuratorUrl ??
    device?.subGroup?.article?.configuratorUrl ??
    null;

// Keeps a button's place in the row when the button isn't shown, so the
// columns (Qty, Reserve, icons) line up in every row.
const IconSlot = () => <span className="icon-button icon-button--placeholder" aria-hidden="true" />;

// Opens an external page (configurator, manufacturer page) in a new tab.
// Without a valid URL it only keeps its slot.
function ConfiguratorButton({ url, title }) {
    const href = safeConfiguratorUrl(url);
    if (!href) return <IconSlot />;
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="icon-button"
            title={title}
            aria-label={title}
            onClick={(e) => e.stopPropagation()}
        >
            <FiExternalLink />
        </a>
    );
}

// Number input that keeps its own draft while the user types and only
// reports a value on blur / Enter. Typing no longer triggers a layout
// update per keystroke, an emptied field is not turned into 0 mid-typing,
// and an unchanged value is never sent.
//   Enter  -> commit
//   Escape -> revert to the stored value
//   empty  -> `emptyValue` (null = revert to the stored value),
//             or null when `clearable` (= "no own value", e.g. use the default)
function NumberField({ value, onCommit, emptyValue = null, clearable = false, placeholder, className, ...rest }) {
    const toText = (v) => (v === null || v === undefined ? "" : String(v));
    const [draft, setDraft] = React.useState(toText(value));
    const editing = React.useRef(false);

    // Follow outside changes (undo, recalculation, another view) while not editing.
    React.useEffect(() => {
        if (!editing.current) setDraft(toText(value));
    }, [value]);

    const commit = () => {
        editing.current = false;
        const trimmed = draft.trim();

        let next;
        if (trimmed === "" && clearable) {
            next = null;
        } else if (trimmed === "") {
            if (emptyValue === null) {
                setDraft(toText(value));
                return;
            }
            next = emptyValue;
        } else {
            const parsed = Math.max(0, Math.floor(Number(trimmed)));
            if (!Number.isFinite(parsed)) {
                setDraft(toText(value));
                return;
            }
            next = parsed;
        }

        setDraft(toText(next));
        const current = clearable ? (value ?? null) : (value ?? emptyValue);
        if (next !== current) onCommit?.(next);
    };

    return (
        <input
            {...rest}
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            placeholder={placeholder}
            className={className}
            value={draft}
            onFocus={e => {
                editing.current = true;
                e.target.select();
            }}
            onChange={e => {
                editing.current = true;
                setDraft(e.target.value);
            }}
            onBlur={commit}
            onKeyDown={e => {
                if (e.key === "Enter") {
                    e.currentTarget.blur(); // triggers commit
                } else if (e.key === "Escape") {
                    editing.current = false;
                    setDraft(toText(value));
                    e.currentTarget.blur();
                }
            }}
            // Stop the mouse wheel from silently changing the value while scrolling the tree.
            onWheel={e => e.currentTarget.blur()}
        />
    );
}

const SectionRow = React.memo(function SectionRow({
    row,
    hiddenIds,
    selectedId,
    inSelectedSection,
    showRailToggle,
    defaultUnitsPerRail,  // scenario value, shown as placeholder (0 = no limit)
    configuratorUrl,
    reorderable,
    dragging,
    dropMarker,          // "before" | "after" | null
    onDragStartRow,
    onDragOverRow,
    onDropRow,
    onDragEndRow,
    onQuantityChange,
    onPlaceholderQuantityChange,
    onRailOrientationChange,
    onUnitsPerRailChange,
    onDeleteDevice,
    onSelect,
    onToggleVisibility
}) {
    const { device } = row;
    const id = rowId(device);
    const hidden = hiddenIds.has(id);
    const selected = selectedId === id || !!inSelectedSection;
    const rail = normalizeRail(device.railOrientation);

    const name = device?.subGroup?.type;
    const articleNumber = device?.subGroup?.articleNumber;

    return (
        <div
            className={
                "section-row" +
                (selected ? " section-row--selected" : "") +
                (showRailToggle && rail === RAIL.VERTICAL ? " section-row--vertical" : "") +
                (showRailToggle && rail === RAIL.AUTO ? " section-row--auto" : "") +
                (dragging ? " section-row--dragging" : "") +
                (dropMarker ? ` section-row--drop-${dropMarker}` : "")
            }
            onDragOver={reorderable ? (e) => onDragOverRow?.(e, device.id) : undefined}
            onDrop={reorderable ? (e) => onDropRow?.(e) : undefined}
        >
            {reorderable && (
                <span
                    className="section-row__drag"
                    title="Drag to change the order in this section"
                    draggable
                    onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", String(device.id)); // Firefox needs data
                        const rowEl = e.currentTarget.closest(".section-row");
                        if (rowEl) e.dataTransfer.setDragImage(rowEl, 16, 16);
                        onDragStartRow?.(device.id);
                    }}
                    onDragEnd={() => onDragEndRow?.()}
                >
                    <MdDragIndicator />
                </span>
            )}

            <div className="section-row__info">
                <span
                    onClick={() => onSelect?.(id)}
                    className="section-row__name"
                >
                    {name}
                </span>
                <span className="section-row__article">
                    {articleNumber}
                </span>
            </div>

            <div className="section-row__controls">
                <label className="section-row__field">
                    <span className="section-row__field-label">Qty</span>
                    <NumberField
                        value={device.quantity}
                        className="section-row__input"
                        aria-label={`Quantity of ${name ?? "device"}`}
                        onCommit={v => onQuantityChange?.(device.id, v)}
                    />
                </label>

                <label className="section-row__field">
                    <span className="section-row__field-label">Reserve</span>
                    <NumberField
                        value={device.placeholderQuantity}
                        emptyValue={0}
                        placeholder="0"
                        className="section-row__input"
                        aria-label={`Reserve of ${name ?? "device"}`}
                        onCommit={v => onPlaceholderQuantityChange?.(device.id, v)}
                    />
                </label>

                {/* Flexible only: units per vertical rail. Shown for vertical / auto
                    devices; horizontal rows keep an empty slot so columns line up. */}
                {showRailToggle && (rail !== RAIL.HORIZONTAL ? (
                    <label
                        className="section-row__field"
                        title={
                            "Units per vertical rail - more continue in the next column. " +
                            "Empty = scenario value" +
                            (defaultUnitsPerRail > 0 ? ` (${defaultUnitsPerRail})` : " (no limit)") +
                            ", 0 = no limit."
                        }
                    >
                        <span className="section-row__field-label">Per rail</span>
                        <NumberField
                            value={device.maxUnitsPerRail ?? null}
                            clearable
                            placeholder={defaultUnitsPerRail > 0 ? String(defaultUnitsPerRail) : "\u2013"}
                            className={
                                "section-row__input" +
                                (device.maxUnitsPerRail == null ? " section-row__input--default" : "")
                            }
                            aria-label={`Units per vertical rail of ${name ?? "device"}`}
                            onCommit={v => onUnitsPerRailChange?.(device.id, v)}
                        />
                    </label>
                ) : (
                    <span className="section-row__field section-row__field--placeholder" aria-hidden="true">
                        <span className="section-row__field-label">Per rail</span>
                        <span className="section-row__input" />
                    </span>
                ))}

                {showRailToggle && (
                    <button
                        type="button"
                        title={`${RAIL_LABEL[rail]} – click for ${RAIL_LABEL[nextRail(rail)].toLowerCase()}`}
                        className={"icon-button" + (rail !== RAIL.HORIZONTAL ? " icon-button--active" : "")}
                        onClick={() => onRailOrientationChange?.(device.id, nextRail(rail))}
                    >
                        {rail === RAIL.VERTICAL ? <MdSwapVert /> : rail === RAIL.AUTO ? <MdAutorenew /> : <MdSwapHoriz />}
                    </button>
                )}

                <ConfiguratorButton
                    url={configuratorUrl}
                    title={`Open manufacturer page / data sheet${name ? ` – ${name}` : ""}`}
                />

                <button
                    title="Remove device"
                    className="icon-button"
                    onClick={() => onDeleteDevice?.(device.id)}
                >
                    <FiTrash2 />
                </button>

                <button
                    title={hidden ? "Show" : "Hide"}
                    className="icon-button"
                    onClick={() => onToggleVisibility?.(id)}
                >
                    {hidden ? <HiEyeOff /> : <HiEye />}
                </button>
            </div>
        </div>
    );
});

function CabinetGroup({ partNumber, configuratorUrl, hiddenIds, selectedId, onSelect, onToggleVisibility }) {
    const hidden = hiddenIds.has(ENCLOSURE_ID);
    const selected = selectedId === ENCLOSURE_ID;

    return (
        <div>
            <div className="cabinet-group__header">Enclosure</div>
            <div className="cabinet-group__list">
                <div className={"cabinet-group__item" + (selected ? " cabinet-group__item--selected" : "")}>
                    <span
                        className="cabinet-group__item-name"
                        onClick={() => onSelect?.(ENCLOSURE_ID)}
                    >
                        Enclosure{partNumber ? `: ${partNumber}` : ""}
                    </span>
                    {/* Same slots as a device row (link, delete, hide), so the
                        icons sit in the same columns as the ones below */}
                    <div className="cabinet-group__controls">
                        <ConfiguratorButton url={configuratorUrl} title="Open enclosure configurator" />
                        <IconSlot />
                        <button
                            title={hidden ? "Show in 3D" : "Hide in 3D"}
                            className="icon-button"
                            onClick={() => onToggleVisibility?.(ENCLOSURE_ID)}
                        >
                            {hidden ? <HiEyeOff /> : <HiEye />}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SectionGroup({
    section,
    hiddenIds,
    selectedId,
    showRailToggle,
    defaultUnitsPerRail,
    getConfiguratorUrl,
    onReorder,
    onQuantityChange,
    onPlaceholderQuantityChange,
    onRailOrientationChange,
    onUnitsPerRailChange,
    onSectionRailChange,
    onSectionVisibility,
    onDeleteDevice,
    onSelect,
    onToggleVisibility
}) {
    const [expanded, setExpanded] = React.useState(true);

    // ── Whole-section state: visibility and rail ──────────────────────────
    const rowIds = section.rows.map((r) => rowId(r.device));
    const hiddenCount = rowIds.filter((id) => hiddenIds.has(id)).length;
    const allHidden = rowIds.length > 0 && hiddenCount === rowIds.length;
    const someHidden = hiddenCount > 0 && !allHidden;

    const rails = section.rows.map((r) => normalizeRail(r.device.railOrientation));
    const sectionRail = rails.length && rails.every((r) => r === rails[0]) ? rails[0] : null; // null = mixed

    const toggleSectionVisibility = () => {
        // all hidden -> show all; otherwise (none / some hidden) -> hide all
        onSectionVisibility?.(rowIds, !allHidden);
    };

    const cycleSectionRail = () => {
        // mixed -> everything horizontal first; otherwise the next state for all
        const next = sectionRail === null ? RAIL.HORIZONTAL : nextRail(sectionRail);
        onSectionRailChange?.(section.rows.map((r) => r.device.id), next);
    };

    // ── Drag & drop inside this section only ──────────────────────────────
    // dragId: device being dragged; drop: { id, where: "before" | "after" }.
    // Other sections never get dragId, so they don't accept the drop.
    const reorderable = !!onReorder && section.rows.length > 1;
    const [dragId, setDragId] = React.useState(null);
    const [drop, setDrop] = React.useState(null);

    const resetDrag = React.useCallback(() => { setDragId(null); setDrop(null); }, []);

    const handleDragOver = React.useCallback((e, overId) => {
        if (!dragId) return;                      // drag started in another section
        e.preventDefault();                       // allow dropping here
        e.dataTransfer.dropEffect = "move";
        const rect = e.currentTarget.getBoundingClientRect();
        const where = e.clientY < rect.top + rect.height / 2 ? "before" : "after";
        setDrop((prev) => (prev?.id === overId && prev?.where === where ? prev : { id: overId, where }));
    }, [dragId]);

    const handleDrop = React.useCallback((e) => {
        if (!dragId || !drop) return;
        e.preventDefault();

        const ids = section.rows.map((r) => r.device.id);
        const from = ids.indexOf(dragId);
        let to = ids.indexOf(drop.id) + (drop.where === "after" ? 1 : 0);
        if (from < to) to -= 1;                   // index shifts after removing the dragged row
        resetDrag();
        if (from < 0 || from === to) return;

        ids.splice(from, 1);
        ids.splice(to, 0, dragId);
        onReorder?.(ids);
    }, [dragId, drop, section.rows, onReorder, resetDrag]);

    // Clicking the section name selects the section = all of its devices,
    // in every view (2D, 3D, tree). Clicking it again deselects.
    const sectionId = section.key != null ? sectionSelectionId(section.key) : null;
    const sectionSelected = !!sectionId && selectedId === sectionId;

    return (
        <div>
            <div
                className={
                    "section-group__header" +
                    (sectionSelected ? " section-group__header--selected" : "")
                }
            >
                <button
                    type="button"
                    className="section-group__caret icon-button"
                    title={expanded ? "Collapse" : "Expand"}
                    onClick={() => setExpanded(!expanded)}
                >
                    {expanded ? <FiChevronDown /> : <FiChevronRight />}
                </button>
                <span
                    className="section-group__name"
                    title="Select all devices of this section"
                    onClick={() => sectionId && onSelect?.(sectionId)}
                >
                    {section.name}
                </span>
                <span className="section-group__count">({section.rows.length})</span>

                {/* Same icon columns as the device rows: rail, (link), (delete), hide */}
                <div className="section-group__controls">
                    {showRailToggle && onSectionRailChange && (
                        <button
                            type="button"
                            className={
                                "icon-button" +
                                (sectionRail === null ? " icon-button--mixed" : "") +
                                (sectionRail !== null && sectionRail !== RAIL.HORIZONTAL ? " icon-button--active" : "")
                            }
                            title={
                                sectionRail === null
                                    ? "Mixed rails – click to set the whole section horizontal"
                                    : `Whole section: ${RAIL_LABEL[sectionRail].toLowerCase()} – click for ${RAIL_LABEL[nextRail(sectionRail)].toLowerCase()}`
                            }
                            onClick={cycleSectionRail}
                        >
                            {sectionRail === RAIL.VERTICAL ? <MdSwapVert />
                                : sectionRail === RAIL.AUTO ? <MdAutorenew />
                                : <MdSwapHoriz />}
                        </button>
                    )}
                    <IconSlot />
                    <IconSlot />
                    <button
                        type="button"
                        className={"icon-button" + (someHidden ? " icon-button--mixed" : "")}
                        title={
                            allHidden ? "Show all devices of this section"
                                : someHidden ? `${hiddenCount} of ${rowIds.length} hidden – click to hide all`
                                : "Hide all devices of this section"
                        }
                        onClick={toggleSectionVisibility}
                    >
                        {allHidden ? <HiEyeOff /> : <HiEye />}
                    </button>
                </div>
            </div>

            {expanded && (
                <div className="section-group__body">
                    {section.rows.length === 0 ? (
                        <div className="section-group__empty">No devices in this section</div>
                    ) : (
                        section.rows.map((row) => (
                            <SectionRow
                                key={row.device.id}
                                row={row}
                                hiddenIds={hiddenIds}
                                selectedId={selectedId}
                                inSelectedSection={sectionSelected}
                                showRailToggle={showRailToggle}
                                defaultUnitsPerRail={defaultUnitsPerRail}
                                configuratorUrl={getConfiguratorUrl?.(row.device)}
                                reorderable={reorderable}
                                dragging={dragId === row.device.id}
                                dropMarker={drop?.id === row.device.id && dragId !== row.device.id ? drop.where : null}
                                onDragStartRow={setDragId}
                                onDragOverRow={handleDragOver}
                                onDropRow={handleDrop}
                                onDragEndRow={resetDrag}
                                onQuantityChange={onQuantityChange}
                                onPlaceholderQuantityChange={onPlaceholderQuantityChange}
                                onRailOrientationChange={onRailOrientationChange}
                                onUnitsPerRailChange={onUnitsPerRailChange}
                                onDeleteDevice={onDeleteDevice}
                                onSelect={onSelect}
                                onToggleVisibility={onToggleVisibility}
                            />
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

// Purely data-driven: SectionName -> [ drag handle (Flexible only), subGroup
// name, part number, Qty, Reserve, rail (Flexible only), configurator link
// (only when the article has one), delete, hide ]. Works identically for the
// 2D canvas and the 3D canvas — it only deals in device.subGroup.id, never in
// scene objects.
//
// Rail toggle and drag & drop come from LayoutContext by default (shown only
// when the scenario is Flexible Oriented); all can be overridden through props.
export default function DeviceSectionTree({
    enclosurePartNumber = "",
    enclosureConfiguratorUrl,
    getConfiguratorUrl,
    sectionPlacements = [],
    sections = [],
    devices = [],
    onQuantityChange,
    onPlaceholderQuantityChange,
    onRailOrientationChange,
    onReorderDevices,
    showRailToggle,
    onDeleteDevice,
    hiddenIds,
    selectedId,
    onSelect,
    onToggleVisibility
}) {
    const layout = useLayout();
    const flexible = showRailToggle ?? !!layout?.useFlexible;
    const railChange = onRailOrientationChange ?? layout?.handleRailOrientationChange;
    const reorder = onReorderDevices ?? layout?.handleReorderDevices;
    const sectionRailChange = layout?.handleRailOrientationChangeMany;
    const unitsPerRailChange = layout?.handleUnitsPerRailChange;
    const defaultUnitsPerRail =
        Number(layout?.selectedScenario?.settings?.flexibleOptions?.maxUnitsPerVerticalRail) || 0;

    // Hide / show many devices at once. Uses LayoutContext's bulk setter
    // (one update, one broadcast); without it, toggles only the ids whose
    // state actually changes.
    const setSectionVisibility = React.useCallback((ids, hide) => {
        if (layout?.setHiddenMany) {
            layout.setHiddenMany(ids, hide);
            return;
        }
        ids.filter((id) => hiddenIds.has(id) !== hide).forEach((id) => onToggleVisibility?.(id));
    }, [layout, hiddenIds, onToggleVisibility]);

    // Links come from LayoutContext (current articles) unless passed in
    const configuratorUrlFor = getConfiguratorUrl ?? layout?.getConfiguratorUrl ?? defaultGetConfiguratorUrl;
    const enclosureLink = enclosureConfiguratorUrl ?? layout?.enclosureConfiguratorUrl ?? null;

    const groupedSections = React.useMemo(
        () => buildSections(sectionPlacements, sections, devices, flexible),
        [sectionPlacements, sections, devices, flexible]
    );

    return (
        <div className="device-section-tree">
            <h3 className="device-section-tree__title">Layout Tree</h3>

            <CabinetGroup
                partNumber={enclosurePartNumber}
                configuratorUrl={enclosureLink}
                hiddenIds={hiddenIds}
                selectedId={selectedId}
                onSelect={onSelect}
                onToggleVisibility={onToggleVisibility}
            />

            <hr className="device-section-tree__divider" />

            {groupedSections.length === 0 && (
                <div className="device-section-tree__empty">No sections found</div>
            )}
            <h4 className="device-section-tree__subtitle">Sections</h4>
            {groupedSections.map((section, i) => (
                <SectionGroup
                    // Stable key so a section doesn't remount (and lose an
                    // in-progress edit) when the list reorders.
                    key={section.key ?? `${section.name}-${i}`}
                    section={section}
                    hiddenIds={hiddenIds}
                    selectedId={selectedId}
                    showRailToggle={flexible}
                    defaultUnitsPerRail={defaultUnitsPerRail}
                    getConfiguratorUrl={configuratorUrlFor}
                    onReorder={flexible ? reorder : undefined}
                    onQuantityChange={onQuantityChange}
                    onPlaceholderQuantityChange={onPlaceholderQuantityChange}
                    onRailOrientationChange={railChange}
                    onUnitsPerRailChange={unitsPerRailChange}
                    onSectionRailChange={flexible ? sectionRailChange : undefined}
                    onSectionVisibility={setSectionVisibility}
                    onDeleteDevice={onDeleteDevice}
                    onSelect={onSelect}
                    onToggleVisibility={onToggleVisibility}
                />
            ))}
        </div>
    );
}
